/**
 * Login Redirect Utility
 * Handles storing and retrieving the previous page URL for login redirects
 */

/**
 * Store the current page URL before navigating to login
 * @param {string} currentPath - The current page path
 */
export const storeLoginRedirect = (currentPath) => {
  try {
    // Store the current path in sessionStorage
    sessionStorage.setItem('loginRedirectPath', currentPath);
  } catch (error) {
    console.warn('Failed to store login redirect path:', error);
  }
};

/**
 * Get the stored redirect path for login
 * @returns {string} - The stored redirect path or default fallback
 */
export const getLoginRedirectPath = () => {
  try {
    const storedPath = sessionStorage.getItem('loginRedirectPath');
    
    // Return stored path or fallback to home
    return storedPath || '/';
  } catch (error) {
    console.warn('Failed to get login redirect path:', error);
    return '/';
  }
};

/**
 * Clear the stored login redirect path
 */
export const clearLoginRedirectPath = () => {
  try {
    sessionStorage.removeItem('loginRedirectPath');
  } catch (error) {
    console.warn('Failed to clear login redirect path:', error);
  }
};

/**
 * Navigate to login with redirect handling
 * @param {string} currentPath - The current page path
 * @param {function} navigate - React Router navigate function
 */
export const navigateToLogin = (currentPath, navigate) => {
  // Store the current path for redirect
  storeLoginRedirect(currentPath);
  
  // Navigate to login
  navigate('/login');
};

/**
 * Handle login close/exit with redirect
 * @param {function} navigate - React Router navigate function
 * @param {string} fallbackPath - Optional fallback path if no stored path exists
 */
export const handleLoginClose = (navigate, fallbackPath = '/') => {
  const redirectPath = getLoginRedirectPath();
  
  // Clear the stored path
  clearLoginRedirectPath();
  
  // Navigate to the stored path or fallback
  navigate(redirectPath);
};

/**
 * Navigate to register with redirect handling
 * @param {string} currentPath - The current page path
 * @param {function} navigate - React Router navigate function
 */
export const navigateToRegister = (currentPath, navigate) => {
  // Store the current path for redirect
  storeLoginRedirect(currentPath);
  
  // Navigate to register
  navigate('/register');
};

/**
 * Handle register close/exit with redirect
 * @param {function} navigate - React Router navigate function
 * @param {string} fallbackPath - Optional fallback path if no stored path exists
 */
export const handleRegisterClose = (navigate, fallbackPath = '/') => {
  const redirectPath = getLoginRedirectPath();
  
  // Clear the stored path
  clearLoginRedirectPath();
  
  // Navigate to the stored path or fallback
  navigate(redirectPath);
};

/**
 * "Continue as guest" sessions store a real `authToken`/`authUser` (with
 * `is_guest: true`) so the rest of the app can treat them like a logged-in
 * user for read access. That's exactly what breaks navigating to `/login`
 * for them: `GuestMiddleware` (routes/middleware/GuestMiddleware.js) sees
 * `authUser` present and immediately bounces `/login` back to `/dashboard`,
 * which looks like the sign-in button "does nothing". Clear the guest's
 * session first so the login page actually renders, then proceed exactly
 * like `navigateToLogin`.
 * @param {string} currentPath - The current page path
 * @param {function} navigate - React Router navigate function
 */
export const logoutGuestAndNavigateToLogin = (currentPath, navigate) => {
  try { localStorage.removeItem('authToken'); } catch (error) { /* ignore */ }
  try { localStorage.removeItem('authUser'); } catch (error) { /* ignore */ }
  try { sessionStorage.clear(); } catch (error) { /* ignore */ }

  navigateToLogin(currentPath, navigate);
};

/**
 * The one shared "clicked Upgrade/Unlock while logged out or guest" landing
 * flow, used by every upgrade entry point in the app (AppsTrackingPlanModal's
 * plan/trial click handlers, and any locked-content "Unlock" CTA). Unlike
 * navigateToRegister (which just returns the visitor to wherever they were),
 * this always points post-signup at the plans/billing page — picking a plan
 * is a clear signal of intent to subscribe, so we get them straight to
 * checkout instead of dropping them back on the page they unlocked from.
 * Register.js reads this back via getLoginRedirectPath() after a successful
 * sign-up; since ONLY this function (and nothing on the plain sign-up/sign-in
 * path) ever stores '/profile/plans' here, a normal registration never gets
 * redirected to billing — only one that started from an upgrade/unlock click.
 *
 * A guest's existing session is cleared first, same reason
 * logoutGuestAndNavigateToLogin does it for /login: GuestMiddleware would
 * otherwise see the guest's authUser and bounce /register straight back to
 * /dashboard before the visitor ever saw the sign-up form.
 * @param {function} navigate - React Router navigate function
 * @param {boolean} isGuest - whether the current visitor is a guest session
 *   (vs. fully anonymous, which has nothing to clear)
 */
export const navigateToRegisterForUpgrade = (navigate, isGuest = false) => {
  if (isGuest) {
    try { localStorage.removeItem('authToken'); } catch (error) { /* ignore */ }
    try { localStorage.removeItem('authUser'); } catch (error) { /* ignore */ }
    try { sessionStorage.clear(); } catch (error) { /* ignore */ }
  }
  storeLoginRedirect('/profile/plans');
  navigate('/register');
};

/**
 * Email sign-up needs a verification click before the account can log in,
 * and that link opens in a NEW tab — whose sessionStorage (where
 * storeLoginRedirect lives) is empty. So a sign-up started from somewhere
 * the visitor wants to come back to (e.g. the shared report's sign-in modal)
 * parks that path in localStorage instead; VerifyUser.js hands it on to the
 * new tab's login redirect once the account is verified. Scoped to the
 * signed-up email and expired after a day so it can't hijack an unrelated
 * verification later.
 */
const PENDING_VERIFICATION_REDIRECT_KEY = 'pendingVerificationRedirect';
const PENDING_VERIFICATION_REDIRECT_TTL_MS = 24 * 60 * 60 * 1000;

export const storePendingVerificationRedirect = (path, email = '') => {
  try {
    localStorage.setItem(
      PENDING_VERIFICATION_REDIRECT_KEY,
      JSON.stringify({ path, email: (email || '').toLowerCase(), ts: Date.now() })
    );
  } catch (error) {
    console.warn('Failed to store pending verification redirect:', error);
  }
};

/**
 * Read and clear the parked path. Returns null when there's none, it has
 * expired, or it belongs to a different email than the one just verified.
 * @param {string} verifiedEmail - the email the verification was for, if known
 */
export const consumePendingVerificationRedirect = (verifiedEmail = '') => {
  try {
    const raw = localStorage.getItem(PENDING_VERIFICATION_REDIRECT_KEY);
    localStorage.removeItem(PENDING_VERIFICATION_REDIRECT_KEY);
    if (!raw) return null;
    const { path, email, ts } = JSON.parse(raw);
    if (!path || Date.now() - ts > PENDING_VERIFICATION_REDIRECT_TTL_MS) return null;
    if (email && verifiedEmail && email !== verifiedEmail.toLowerCase()) return null;
    return path;
  } catch (error) {
    return null;
  }
};

/**
 * Where to send the user once Stripe checkout returns to /payment-success or
 * /payment-cancel. Stripe redirects in the same tab, so sessionStorage
 * survives the round-trip and stays scoped to this tab. Only checkouts
 * started with a return path set it (see AppsTrackingPlanModal's
 * `returnPath`); every other checkout clears it, and it expires after an
 * hour, so an abandoned checkout can't redirect a later unrelated purchase.
 */
const POST_PAYMENT_REDIRECT_KEY = 'postPaymentRedirect';
const POST_PAYMENT_REDIRECT_TTL_MS = 60 * 60 * 1000;

export const storePostPaymentRedirect = (path) => {
  try {
    sessionStorage.setItem(POST_PAYMENT_REDIRECT_KEY, JSON.stringify({ path, ts: Date.now() }));
  } catch (error) {
    console.warn('Failed to store post-payment redirect:', error);
  }
};

export const getPostPaymentRedirect = () => {
  try {
    const raw = sessionStorage.getItem(POST_PAYMENT_REDIRECT_KEY);
    if (!raw) return null;
    const { path, ts } = JSON.parse(raw);
    if (!path || Date.now() - ts > POST_PAYMENT_REDIRECT_TTL_MS) return null;
    return path;
  } catch (error) {
    return null;
  }
};

export const clearPostPaymentRedirect = () => {
  try {
    sessionStorage.removeItem(POST_PAYMENT_REDIRECT_KEY);
  } catch (error) {
    /* ignore */
  }
};

const loginRedirectUtils = {
  storeLoginRedirect,
  getLoginRedirectPath,
  clearLoginRedirectPath,
  navigateToLogin,
  handleLoginClose,
  navigateToRegister,
  handleRegisterClose,
  logoutGuestAndNavigateToLogin,
  navigateToRegisterForUpgrade,
  storePendingVerificationRedirect,
  consumePendingVerificationRedirect,
  storePostPaymentRedirect,
  getPostPaymentRedirect,
  clearPostPaymentRedirect,
};

export default loginRedirectUtils;
